<!-- src/views/WhatsNew.vue -->
<!-- Product changelog. Newest first. Dates are fixed strings (no Date.now) so the
     page is stable and auditable across renders/deploys. -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  interface Entry {
    date: string;
    title: string;
    body: string;
  }

  // English-first changelog; localize entries later. Title/link are i18n'd.
  const entries: Entry[] = [
    {
      date: 'July 2026',
      title: 'Data-residency regions',
      body: 'Choose where your secrets live — Asia-Pacific, Europe, North America, or South America. Each region is a fully independent environment with its own storage and encryption key, so a secret never leaves the region it was created in. Switch regions from the footer.',
    },
    {
      date: 'July 2026',
      title: 'Clearer composer on mobile',
      body: 'Larger touch targets for the expiry picker, a taller editor that keeps the Create button in view, and inline hints that spell out exactly what expiry and passphrase do.',
    },
    {
      date: 'June 2026',
      title: 'Dark mode',
      body: 'A refined dark theme across the whole app, matched to the BurnLink infrastructure look. Toggle it any time from the footer — the light and dark views are the same interface, re-skinned.',
    },
    {
      date: 'June 2026',
      title: 'Authenticated encryption',
      body: 'Secrets are protected with modern authenticated encryption, so their contents cannot be read or silently tampered with. You do not have to trust us with what is inside — by design, you do not.',
    },
  ];

  const h2 = 'text-lg font-semibold text-gray-900 dark:text-gray-100';
  const meta = 'text-xs font-medium uppercase tracking-wider text-brand-600 dark:text-brand-400';
  const body = 'mt-2 text-base leading-7 text-gray-600 dark:text-gray-300';
</script>

<template>
  <div class="container mx-auto max-w-3xl px-4 py-8">
    <article aria-labelledby="whatsnew-title">
      <header>
        <h1
          id="whatsnew-title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          {{ t('web.footer.whats_new') }}
        </h1>
        <p class="mt-2 text-base leading-7 text-gray-500 dark:text-gray-400">
          {{ t('web.whatsnew.subtitle') }}
        </p>
      </header>

      <ol class="mt-10 space-y-10 border-l border-gray-200 pl-6 dark:border-gray-700">
        <li
          v-for="(entry, i) in entries"
          :key="i"
          class="relative">
          <span
            class="absolute -left-[1.9rem] top-1 size-3 rounded-full bg-brand-500 ring-4 ring-white dark:bg-brand-400 dark:ring-gray-900"
            aria-hidden="true"></span>
          <p :class="meta">{{ entry.date }}</p>
          <h2 :class="[h2, 'mt-1']">{{ entry.title }}</h2>
          <p :class="body">{{ entry.body }}</p>
        </li>
      </ol>
    </article>
  </div>
</template>
